import { clamp } from '../Math.js';
import { AOQualityPreset, CameraMode } from '../api.js';
import { texture_to_base64 } from './AssetManager.js';
import { VIEWER_DEFAULT_COMPATIBILITY_FRAMEBUFFER_TYPE, VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE, VIEWER_WANTED_MSAA_SAMPLES, Viewer } from './Viewer.js';
import * as Utils from './utility/utils.js';
import { RenderLayerMask, VIEWER_INTERNAL_RENDER_LAYER_SCREENSHOT } from './RenderLayer.js';
import { RenderView } from './RenderView.js';
import { InteractionSystem } from './InteractionSystem.js';
function get_bitmap_of_render_layer(layer) {
    // We could in theory use all 32 bits, but Babylon doesn't support using the last bit.
    if (layer.bit < 0 || layer.bit >= 31) {
        console.error('Invalid render layer');
        return 0;
    }
    else {
        return 1 << layer.bit;
    }
}
function verify_aspect_ratio(aspect_ratio, projection_matrix, projection_type, max_error) {
    let projection_aspect = 0.0;
    if (projection_type === CameraMode.ORTHOGRAPHIC) {
        projection_aspect = projection_matrix[1].y / projection_matrix[0].x;
    }
    else if (projection_type === CameraMode.PERSPECTIVE) {
        const projection_width = 2.0 / projection_matrix[0].x;
        const projection_height = 2.0 / projection_matrix[1].y;
        projection_aspect = projection_width / projection_height;
    }
    else {
        // Should this be warned? Mostly for us I guess.
        console.warn('Missing case for given perspective mode.');
        return false;
    }
    const error = Math.abs(aspect_ratio / projection_aspect - 1.0);
    return error <= max_error;
}
// A list of screenshot promises to ensure we only run one screenshot at a time
const active_screenshots = new Set();
// TODO: Improve configuration with better logging system
const enable_screenshot_debug_logging = false;
let debug_screenshot_counter = 0;
export function take_screenshot(viewer, projection_matrix, projection_type, view_matrix, width, height, settings, mesh_render_list, experimental_settings) {
    // Fetch a promise for the screenshot ahead of us
    const screenshot_queue = Promise.allSettled(active_screenshots);
    const debug_screenshot_number = debug_screenshot_counter++;
    if (enable_screenshot_debug_logging) {
        console.info('Queueing screenshot: ' + debug_screenshot_number);
        console.info('  width: ' + width + ', height: ' + height + ', # meshes: ' + (mesh_render_list === null || mesh_render_list === void 0 ? void 0 : mesh_render_list.length));
    }
    const screenshot_promise = new Promise((resolve) => {
        // Wait for previous screenshots to finish
        screenshot_queue.then(() => {
            var _a, _b;
            if (enable_screenshot_debug_logging) {
                console.info('Starting screenshot: ' + debug_screenshot_number);
            }
            const rendering_lock = viewer.acquire_rendering_pause_lock();
            if (viewer.get_config().verbose_warnings &&
                !verify_aspect_ratio(width / height, projection_matrix, projection_type, 2.0 / Math.min(width, height))) {
                console.warn('Capturing screenshot with non-square pixels (projection matrix aspect ratio mismatch from resolution).\nThis results in a skewed/distorted image.');
            }
            if (settings.mime_type === 'image/jpg') {
                settings.mime_type = 'image/jpeg';
                console.warn('Change MIME type to correct spelling image/jpeg');
            }
            if (settings.image_compression !== undefined) {
                if (settings.image_compression < 0.0 || settings.image_compression > 1.0) {
                    console.warn('Invalid image compression for screenshot: value needs to be in the range 0.0 - 1.0');
                    settings.image_compression = clamp(settings.image_compression, 0.0, 1.0);
                }
            }
            const view = new RenderView(viewer, new InteractionSystem(viewer, false), width, height, viewer.is_rendering_to_float_framebuffer()
                ? VIEWER_DEFAULT_FLOAT_FRAMEBUFFER_TYPE
                : VIEWER_DEFAULT_COMPATIBILITY_FRAMEBUFFER_TYPE, VIEWER_WANTED_MSAA_SAMPLES);
            // Load the scene's AO settings as base, but if we have no active accumulation buffer, create defaults.
            const default_screenshot_ao_settings = Object.assign(Object.assign({}, view.postprocess_manager.ao_settings), { quality: AOQualityPreset.SCREENSHOT });
            const ao_settings = Utils.update_from_partial(viewer._default_view !== null ? Object.assign({}, viewer._default_view.postprocess_manager.ao_settings) : default_screenshot_ao_settings, (_a = settings.ao_settings) !== null && _a !== void 0 ? _a : {});
            const default_bloom_settings = Object.assign({}, view.postprocess_manager.bloom_settings);
            const bloom_settings = Utils.update_from_partial(viewer._default_view !== null ? Object.assign({}, viewer._default_view.postprocess_manager.bloom_settings) : default_bloom_settings, (_b = settings.bloom_settings) !== null && _b !== void 0 ? _b : {});
            view.engine_camera.set_view_matrix(view_matrix);
            view.engine_camera.set_projection_matrix(projection_matrix, projection_type);
            if (experimental_settings) {
                if (experimental_settings.edge_sketch) {
                    view.postprocess_manager.enable_edge_sketch_effect();
                    ao_settings.enabled = false;
                }
            }
            view.postprocess_manager.set_bloom(bloom_settings.enabled, bloom_settings.intensity);
            view.postprocess_manager.enable_temporal_effects(ao_settings.enabled);
            view.postprocess_manager.set_temporal_ao_parameters(ao_settings);
            const accumulator = view.postprocess_manager.get_active_accumulation_buffer();
            // Warm-up render to create any effects needed, so that we can wait for them to finish compiling in the next step.
            // Otherwise we might get unfinished or transparent screenshots.
            viewer.prepare_scene_for_setup();
            viewer.prepare_scene_for_render();
            view.engine_camera.set_view_matrix(view_matrix);
            view.engine_camera.set_projection_matrix(projection_matrix, projection_type);
            view.render(mesh_render_list);
            // Wait for any pending shaders to finish compiling.
            // It's important to have done a warm-up render before to create & initialize any shaders needed.
            const shader_compiled_promises = Promise.allSettled([viewer.get_effects_compiled_promise()]);
            shader_compiled_promises.then(() => {
                // Reset accumulator ahead of our actual rendering
                accumulator.reset_stable_frames();
                if (mesh_render_list !== null) {
                    // Here we have an internal render layer for screenshots, to temporarily assign the objects to that layer, and render only it.
                    // While the Views allow a custom render list, we still need this for shadows.
                    // We restore the meshes further down.
                    // IMPORTANT NOTE! This has to be done just before rendering, if we await anything after this but before rendering, we get synchhronization issues.
                    view.set_render_layer_mask(new RenderLayerMask([VIEWER_INTERNAL_RENDER_LAYER_SCREENSHOT]));
                    for (let i = 0; i < mesh_render_list.length; i++) {
                        mesh_render_list[i].layerMask =
                            mesh_render_list[i].layerMask |
                                get_bitmap_of_render_layer(VIEWER_INTERNAL_RENDER_LAYER_SCREENSHOT);
                    }
                }
                const restore_shadow_bitmask = viewer.get_shadows_render_layer_mask();
                viewer.set_shadows_render_layer_mask(new RenderLayerMask([VIEWER_INTERNAL_RENDER_LAYER_SCREENSHOT]));
                viewer.prepare_scene_for_setup();
                viewer.prepare_scene_for_render();
                view.engine_camera.set_view_matrix(view_matrix);
                view.engine_camera.set_projection_matrix(projection_matrix, projection_type);
                while (accumulator.keep_rendering()) {
                    view.render(mesh_render_list);
                }
                if (mesh_render_list !== null) {
                    // Restore the meshes so they don't have our internal render layer.
                    // IMPORTANT NOTE! This has to be done just after rendering, if we await anything before doing this after rendering, we get synchhronization issues.
                    for (let i = 0; i < mesh_render_list.length; i++) {
                        mesh_render_list[i].layerMask =
                            mesh_render_list[i].layerMask &
                                (~get_bitmap_of_render_layer(VIEWER_INTERNAL_RENDER_LAYER_SCREENSHOT));
                    }
                }
                viewer.set_shadows_render_layer_mask(restore_shadow_bitmask);
                // Needs to be done before the disposing the view
                // We intentionally use the accumulation buffer's render target here, as we need to create the texture from a resolved render target (no MSAA)
                // The accumulation buffer will be resolved, and it should also contain all postprocessing effects.
                // Thus, we use it here instead of the View's normal framebuffer, which would be the natural choice.
                const texture_base64_data = texture_to_base64(view.postprocess_manager.get_destination_texture(), settings.mime_type, settings.image_compression);
                texture_base64_data.then((data) => {
                    Viewer.verify_screenshot_mime_type(data, settings.mime_type);
                    view.dispose();
                    viewer.release_rendering_pause_lock(rendering_lock);
                    resolve(texture_base64_data);
                });
            });
        });
    });
    active_screenshots.add(screenshot_promise);
    screenshot_promise.then(() => {
        active_screenshots.delete(screenshot_promise);
        if (enable_screenshot_debug_logging) {
            console.info('Finished screenshot: ' + debug_screenshot_number);
        }
    });
    return screenshot_promise;
}
//# sourceMappingURL=Screenshots.js.map